import { Shield, Eye, CreditCard } from 'lucide-react';
import { GithubIcon } from '@/components/icons/BrandIcons';
import { useGitHubLogin } from '@/hooks/useGitHubLogin';

export default function TrustHero() {
  const { isLoading, handleGitHubLogin } = useGitHubLogin();

  return (
    <section
      className="relative bg-gradient-to-br from-primary-600 to-primary-700 py-20 overflow-hidden"
    >
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Copy */}
          <div className="text-center lg:text-left">
            <p
              className="text-xs sm:text-sm uppercase tracking-widest text-white/60 mb-4 animate-hero-fade-in animation-delay-100"
            >
              For engineering leaders who need answers, not dashboards
            </p>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6 animate-hero-fade-in animation-delay-100"
            >
              See Why Features Aren't Shipping Faster
            </h1>
            <p
              className="text-lg sm:text-xl text-white/80 mb-8 max-w-xl mx-auto lg:mx-0 animate-hero-fade-in animation-delay-200"
            >
              Get your cycle time breakdown, PR bottlenecks, and team health score in 5 minutes - from GitHub data you already have.
            </p>

            {/* CTA Button */}
            <button
              onClick={() => handleGitHubLogin('hero')}
              disabled={isLoading}
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-primary-700 rounded-xl text-lg font-semibold hover:bg-white/95 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed mb-8 animate-scale-in animation-delay-300"
              style={{ animationFillMode: 'both' }}
            >
              <GithubIcon className="w-6 h-6" aria-hidden="true" />
              {isLoading ? 'Connecting...' : 'Find Your #1 Bottleneck'}
            </button>

            {/* Outcome promise + approved, outcome-bearing customer evidence — above the fold */}
            <p className="text-white/70 text-sm mb-2 animate-hero-fade-in animation-delay-350">
              Most teams find their biggest review bottleneck in the first sync
            </p>
            {/* Trimmed to verbatim fragments (finding + result). Do not reword an
                approved customer quote; shorten it with an ellipsis instead. */}
            <p className="text-white/60 text-xs italic mb-4 animate-hero-fade-in animation-delay-350">
              &ldquo;Almost a third of our PRs were just sitting there waiting on the same two
              reviewers... the difference was noticeable within a couple weeks.&rdquo;
              <span className="not-italic text-white/50"> - Robert Striker, VP of Engineering at Article Fiesta</span>
            </p>

            {/* Trust Badges */}
            <div
              className="flex flex-col sm:flex-row flex-wrap justify-center lg:justify-start gap-4 sm:gap-6 text-white/80 text-sm animate-hero-fade-in animation-delay-400"
            >
              <div className="flex items-center justify-center lg:justify-start gap-2">
                <Eye className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                <span>Your code stays yours - we never read your files</span>
              </div>
              <div className="flex items-center justify-center lg:justify-start gap-2">
                <Shield className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                <span>Read-only GitHub App</span>
              </div>
              <div className="flex items-center justify-center lg:justify-start gap-2">
                <CreditCard className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                <span>No credit card required</span>
              </div>
            </div>
          </div>

          {/* Right: Dashboard Screenshot */}
          <div
            className="relative animate-hero-fade-in animation-delay-500"
          >
            {/* Floating stat badge — Velocity Score */}
            <div className="hidden md:block absolute -top-2 -right-2 bg-gray-900/95 rounded-xl px-4 py-3 shadow-lg border border-primary-500/30 z-10">
              <div className="text-xs text-gray-400 mb-0.5">Velocity Score</div>
              <div className="text-2xl font-bold text-primary-400">83<span className="text-sm text-gray-500 ml-0.5">/100</span></div>
            </div>

            {/* Floating stat badge — Cycle Time */}
            <div className="hidden md:block absolute bottom-8 -left-3 bg-gray-900/95 rounded-xl px-4 py-3 shadow-lg border border-cyan-500/30 z-10">
              <div className="text-xs text-gray-400 mb-0.5">Cycle Time</div>
              <div className="text-2xl font-bold text-cyan-400">1.8<span className="text-sm text-gray-500 ml-0.5">days</span></div>
            </div>

            {/* Floating stat badge — PRs Merged */}
            <div className="hidden lg:block absolute top-10 left-2 bg-gray-900/95 rounded-xl px-3 py-2 shadow-lg border border-green-500/30 z-10">
              <div className="text-xs text-gray-400 mb-0.5">PRs Merged</div>
              <div className="text-lg font-bold text-green-400">63<span className="text-sm text-gray-500 ml-0.5">/week</span></div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 shadow-2xl border border-white/10 animate-subtle-float">
              <img
                src="/images/screenshots/dashboard-velocity-graph.webp"
                alt="CodePulse Engineering Health Dashboard showing velocity metrics, cycle time breakdown, and team performance scores"
                className="w-full rounded-xl object-cover object-[30%_25%]"
              />
            </div>

            {/* Decorative elements */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-primary-500/30 rounded-full blur-2xl" />
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-primary-800/40 rounded-full blur-2xl" />
          </div>
        </div>
      </div>
    </section>
  );
}
